import { RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { SpaceDiff, RankedSpace } from "../types";

interface SpaceDataDiffModalProps {
  diff: SpaceDiff;
  onClose: () => void;
  onAccept: (fresh: RankedSpace) => void;
}

export function SpaceDataDiffModal({
  diff,
  onClose,
  onAccept,
}: SpaceDataDiffModalProps) {
  const { freshSpace, fields } = diff;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden gap-0"
        showCloseButton={false}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4" style={{ background: "#1A1814" }}>
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "rgba(245,158,11,0.15)" }}
              >
                <RefreshCw className="w-4 h-4" style={{ color: "#F59E0B" }} />
              </div>
              <DialogTitle
                className="text-sm font-semibold leading-snug"
                style={{ color: "white" }}
              >
                {freshSpace.roomName}
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 mt-0.5 transition-opacity hover:opacity-60"
              style={{ color: "rgba(255,255,255,0.5)" }}
            >
              ✕
            </button>
          </div>
          {freshSpace.spaceName && (
            <p
              className="text-xs ml-10"
              style={{ color: "rgba(255,255,255,0.45)" }}
            >
              {freshSpace.spaceName}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4" style={{ background: "#FDFBF7" }}>
          <DialogDescription className="text-sm" style={{ color: "#43403D" }}>
            Esta sala teve atualizações desde a criação da proposta. Confira as
            diferenças abaixo e decida se deseja atualizar os dados.
          </DialogDescription>

          {/* Diff table */}
          <div
            className="rounded-lg overflow-hidden border"
            style={{ borderColor: "#E8E6E3" }}
          >
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: "#F0EEEB" }}>
                  <th
                    className="text-left px-3 py-2 font-semibold"
                    style={{ color: "#78736D", width: "30%" }}
                  >
                    Campo
                  </th>
                  <th
                    className="text-left px-3 py-2 font-semibold"
                    style={{ color: "#78736D", width: "35%" }}
                  >
                    Anterior
                  </th>
                  <th
                    className="text-left px-3 py-2 font-semibold"
                    style={{ color: "#78736D", width: "35%" }}
                  >
                    Atual
                  </th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr
                    key={f.field}
                    style={{
                      background: i % 2 === 0 ? "white" : "#FDFBF7",
                      borderTop: "1px solid #F0EEEB",
                    }}
                  >
                    <td
                      className="px-3 py-2 font-medium"
                      style={{ color: "#43403D" }}
                    >
                      {f.label}
                    </td>
                    <td
                      className="px-3 py-2 line-through"
                      style={{ color: "#9E9A94" }}
                    >
                      {f.previous}
                    </td>
                    <td
                      className="px-3 py-2 font-medium"
                      style={{ color: "#1A1814" }}
                    >
                      {f.current}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-[#ECEAE6]"
              style={{ background: "#F0EEEB", color: "#43403D" }}
            >
              Manter dados originais
            </button>
            <button
              type="button"
              onClick={() => onAccept(freshSpace)}
              className="flex-1 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ background: "#FFD900", color: "#1A1814" }}
            >
              Atualizar dados
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
